import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { Card, Badge, Button } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import {
  FileText,
  Download,
  ShieldCheck,
  Globe,
  Activity,
  BarChart,
  Users,
  AlertTriangle,
} from 'lucide-react';

interface ComplianceReportData {
  report_type: string;
  period: {
    start_date: string;
    end_date: string;
    duration: string;
  };
  organization: {
    id: string;
    name: string;
    slug: string;
    billing_email: string;
    data_region: string;
    members_count: number;
  };
  data_subject_requests: {
    total_requests: number;
    export_requests: number;
    deletion_requests: number;
    access_requests: number;
    completed_requests: number;
    pending_requests: number;
    average_response_time: string;
  };
  data_breaches: Array<{
    id: string;
    title: string;
    description: string;
    severity: string;
    affected_records: number;
    start_date: string;
    end_date: string;
    status: string;
    reported_to_authorities: boolean;
    notification_date?: string;
  }>;
  security_incidents: Array<{
    id: string;
    title: string;
    description: string;
    severity: string;
    start_date: string;
    status: string;
  }>;
  third_party_transfers: Array<{
    provider: string;
    purpose: string;
    data_categories: string[];
    transfer_count: number;
    last_transfer_date: string;
    data_region: string;
    safeguards: string;
  }>;
  storage_locations: Array<{
    data_type: string;
    location: string;
    region: string;
    provider: string;
    encryption: string;
  }>;
  compliance_metrics: {
    data_processed_gb: number;
    active_backups: number;
    backup_success_rate: number;
    average_encryption_level: string;
    policy_violation_count: number;
    audit_log_entries: number;
    compliance_score: number;
  };
  generated_at: string;
}

export default function ComplianceReports() {
  const { t, i18n } = useTranslation('compliance');
  const [selectedReportType, setSelectedReportType] = useState<string>('GDPR_COMPLIANCE_SUMMARY');
  const [dateRange, setDateRange] = useState<string>('12months');
  const [reportData, setReportData] = useState<ComplianceReportData | null>(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = getToken();
  const locale = i18n.resolvedLanguage || i18n.language;
  const formatNumber = (value: number, maximumFractionDigits = 0) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);

  const generateReport = async () => {
    setGenerating(true);
    setError(null);
    try {
      const endDate = new Date();
      const startDate = new Date();

      switch (dateRange) {
        case '1month':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '3months':
          startDate.setMonth(startDate.getMonth() - 3);
          break;
        case '6months':
          startDate.setMonth(startDate.getMonth() - 6);
          break;
        case '12months':
        default:
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
      }

      const response = await fetch('/api/v1/compliance/reports/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          report_type: selectedReportType,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
        }),
      });

      if (response.ok) {
        setReportData(await response.json());
      } else {
        setError(t('reports.errors.generateFailed'));
      }
    } catch (_err) {
      setError(t('reports.errors.connectionFailed'));
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = async () => {
    if (!reportData) return;
    setDownloading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        type: selectedReportType,
        start_date: reportData.period.start_date,
        end_date: reportData.period.end_date,
      });

      const response = await fetch(`/api/v1/compliance/reports/pdf?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compliance-report-${selectedReportType}-${dateRange}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else {
        setError(t('reports.errors.downloadFailed'));
      }
    } catch {
      setError(t('reports.errors.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {t('reports.title')}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('reports.subtitle')}
            </p>
          </div>
        </div>

        {/* Report Configuration */}
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_auto] gap-4 items-end">
            <div className="w-full min-w-0">
              <label
                htmlFor="compliance-standard"
                className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
              >
                {t('reports.standard')}
              </label>
              <select
                id="compliance-standard"
                value={selectedReportType}
                onChange={(e) => {
                  setSelectedReportType(e.target.value);
                  setReportData(null);
                  setError(null);
                }}
                disabled={generating || downloading}
                className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 outline-none"
              >
                <option value="GDPR_COMPLIANCE_SUMMARY">{t('reports.gdprSummary')}</option>
                <option value="LGPD_COMPLIANCE_SUMMARY">{t('reports.lgpdSummary')}</option>
              </select>
            </div>

            <div className="w-full min-w-0">
              <label
                htmlFor="compliance-timeframe"
                className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
              >
                {t('reports.timeframe')}
              </label>
              <select
                id="compliance-timeframe"
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setReportData(null);
                  setError(null);
                }}
                disabled={generating || downloading}
                className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 outline-none"
              >
                <option value="1month">{t('reports.last30Days')}</option>
                <option value="3months">{t('reports.lastQuarter')}</option>
                <option value="6months">{t('reports.last6Months')}</option>
                <option value="12months">{t('reports.lastYear')}</option>
              </select>
            </div>

            <Button
              onClick={generateReport}
              isLoading={generating}
              className="w-full md:w-auto h-[46px]"
            >
              {generating ? t('reports.generating') : t('reports.generate')}
            </Button>
          </div>

          {error && (
            <div
              role="alert"
              className="mt-4 p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl text-state-danger-light dark:text-state-danger-dark font-bold text-sm"
            >
              {error}
            </div>
          )}
        </Card>

        {reportData && (
          <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:justify-between sm:items-center">
              <h2 className="text-2xl font-black text-slate-900 dark:text-white">
                {t('reports.resultsTitle')}
              </h2>
              <Button variant="outline" onClick={downloadPDF} isLoading={downloading}>
                <Download className="w-4 h-4 mr-2" aria-hidden="true" />{' '}
                {t('reports.exportPrintable')}
              </Button>
            </div>

            {/* Score Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <ScoreCard
                title={t('reports.metrics.score')}
                value={`${formatNumber(reportData.compliance_metrics.compliance_score)}/100`}
                icon={<ShieldCheck />}
                color="text-state-ok-light dark:text-state-ok-dark"
              />
              <ScoreCard
                title={t('reports.metrics.successRate')}
                value={`${formatNumber(reportData.compliance_metrics.backup_success_rate, 1)}%`}
                icon={<Activity />}
                color="text-state-info-light dark:text-state-info-dark"
              />
              <ScoreCard
                title={t('reports.metrics.activeBackups')}
                value={formatNumber(reportData.compliance_metrics.active_backups)}
                icon={<Users />}
                color="text-accent-text-light dark:text-accent-text-dark"
              />
              <ScoreCard
                title={t('reports.metrics.dataProcessed')}
                value={`${formatNumber(reportData.compliance_metrics.data_processed_gb, 2)} GB`}
                icon={<BarChart />}
                color="text-state-warn-light dark:text-state-warn-dark"
              />
            </div>

            {/* DSR Stats */}
            <Card className="p-6">
              <h3 className="text-lg font-black text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                <FileText className="text-brand-600 dark:text-brand-400" aria-hidden="true" />{' '}
                {t('reports.requestsTitle')}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <StatItem
                  label={t('reports.totalRequests')}
                  value={reportData.data_subject_requests.total_requests}
                />
                <StatItem
                  label={t('reports.exportRequests')}
                  value={reportData.data_subject_requests.export_requests}
                />
                <StatItem
                  label={t('reports.deletionRequests')}
                  value={reportData.data_subject_requests.deletion_requests}
                />
                <StatItem
                  label={t('reports.completed')}
                  value={reportData.data_subject_requests.completed_requests}
                  className="text-state-ok-light dark:text-state-ok-dark"
                />
                <StatItem
                  label={t('reports.averageResponse')}
                  value={reportData.data_subject_requests.average_response_time}
                />
              </div>
            </Card>

            {/* Incidents Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-lg font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <AlertTriangle
                    className="text-state-danger-light dark:text-state-danger-dark"
                    aria-hidden="true"
                  />{' '}
                  {t('reports.breaches')}
                </h3>
                {reportData.data_breaches.length === 0 ? (
                  <p className="text-sm font-medium text-text-muted-light dark:text-text-muted-dark italic">
                    {t('reports.noBreaches')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {reportData.data_breaches.map((breach) => (
                      <div
                        key={breach.id}
                        className="p-3 bg-state-danger-bg-light dark:bg-state-danger-bg-dark rounded-lg border border-state-danger-light/20 dark:border-state-danger-dark/20"
                      >
                        <div className="flex items-start justify-between gap-3 font-bold text-state-danger-light dark:text-state-danger-dark text-sm">
                          <span className="min-w-0 break-words">{breach.title}</span>
                          <Badge
                            className="shrink-0"
                            variant={toneToBadgeVariant[getStatusTone(breach.severity)]}
                          >
                            {t(`reports.severity.${breach.severity.toLowerCase()}`, {
                              defaultValue: breach.severity,
                            })}
                          </Badge>
                        </div>
                        <p className="text-xs text-state-danger-light dark:text-state-danger-dark mt-1">
                          {t(`reports.breachStatus.${breach.status.toLowerCase()}`, {
                            defaultValue: breach.status,
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <Globe
                    className="text-state-info-light dark:text-state-info-dark"
                    aria-hidden="true"
                  />{' '}
                  {t('reports.transfers')}
                </h3>
                {reportData.third_party_transfers.length === 0 ? (
                  <p className="text-sm font-medium text-text-muted-light dark:text-text-muted-dark italic">
                    {t('reports.noTransfers')}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {reportData.third_party_transfers.map((transfer, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between sm:items-center text-sm border-b border-surface-border-light dark:border-surface-border-dark pb-2 last:border-0"
                      >
                        <div>
                          <p className="font-bold text-slate-900 dark:text-white">
                            {transfer.provider}
                          </p>
                          <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                            {transfer.purpose}
                          </p>
                        </div>
                        <div className="shrink-0 sm:text-right">
                          <p className="font-mono font-bold text-slate-700 dark:text-slate-300">
                            {formatNumber(transfer.transfer_count)}
                          </p>
                          <p className="text-[10px] text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-wide">
                            {t('reports.transferCount', {
                              count: transfer.transfer_count,
                              formattedCount: formatNumber(transfer.transfer_count),
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ScoreCard({
  title,
  value,
  icon,
  color,
}: {
  title: string;
  value: string;
  icon: ReactNode;
  color: string;
}) {
  return (
    <Card className="p-6 flex items-center justify-between">
      <div>
        <p className="text-xs font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-1">
          {title}
        </p>
        <p className="text-2xl font-black text-slate-900 dark:text-white">{value}</p>
      </div>
      <div className={`p-3 rounded-xl bg-surface-2-light dark:bg-surface-2-dark ${color}`}>
        {icon}
      </div>
    </Card>
  );
}

function StatItem({
  label,
  value,
  className = 'text-slate-900 dark:text-white',
}: {
  label: string;
  value: string | number;
  className?: string;
}) {
  return (
    <div className="p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-xl">
      <p className="text-xs font-bold text-text-muted-light dark:text-text-muted-dark mb-1">
        {label}
      </p>
      <p className={`text-xl font-black ${className}`}>{value}</p>
    </div>
  );
}
